import { createAsyncThunk, createSlice, ThunkDispatch } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { apolloClient } from "../../apollo/graphql";
import { RejectWithValueType } from "../auth/types/authType";
import {
  GET_ALL_INVOICES,
  GET_GOVERNMENT_REPORT,
  GET_GOV_REPORT_RATES,
  GET_SELECTED_INVOICE,
  GET_UNINVOICED_DEPARTED_EMPLOYEES,
} from "../../apollo/queries/invoices";
import {
  CANCEL_INVOICE,
  CREATE_INVOICE,
  RECORD_INVOICE_PAYMENT,
  UPSERT_GOV_REPORT_RATE,
} from "../../apollo/mutations/invoices";
import {
  CreateInvoiceArgs,
  GovernmentReport,
  GovernmentReportArgs,
  GovReportRate,
  Invoice,
  InvoiceArgs,
  InvoiceEmployee,
  InvoiceState,
  InvoicesWithCount,
  RecordPaymentArgs,
} from "./types/invoiceTypes";

/** List-affecting mutations evict cached invoice lists so re-fetches can't serve stale data. */
function evictInvoiceCaches() {
  apolloClient.cache.evict({ id: "ROOT_QUERY", fieldName: "invoices" });
  apolloClient.cache.evict({
    id: "ROOT_QUERY",
    fieldName: "uninvoicedDepartedEmployees",
  });
  apolloClient.cache.gc();
}

export const fetchInvoices = createAsyncThunk<
  any,
  InvoiceArgs,
  { rejectValue: RejectWithValueType }
>("invoices/fetchInvoices", async (args, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const { agentId, status, skip, take, refreshList } = args;
    const response = await apolloClient.query({
      query: GET_ALL_INVOICES,
      variables: { agentId, status, skip, take },
      fetchPolicy: refreshList === "refresh" ? "network-only" : "cache-first",
    });
    if (response && response.data && response.data.invoices) {
      return response.data.invoices as InvoicesWithCount;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const getInvoice = createAsyncThunk<
  any,
  string,
  { rejectValue: RejectWithValueType }
>("invoices/getInvoice", async (id, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.query({
      query: GET_SELECTED_INVOICE,
      variables: { id },
      fetchPolicy: "network-only",
    });
    if (response && response.data && response.data.getInvoice) {
      return response.data.getInvoice as Invoice;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const fetchUninvoicedEmployees = createAsyncThunk<
  any,
  string,
  { rejectValue: RejectWithValueType }
>("invoices/fetchUninvoicedEmployees", async (agentId, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.query({
      query: GET_UNINVOICED_DEPARTED_EMPLOYEES,
      variables: { agentId },
      fetchPolicy: "network-only",
    });
    if (response && response.data && response.data.uninvoicedDepartedEmployees) {
      return response.data.uninvoicedDepartedEmployees as InvoiceEmployee[];
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const createInvoice = createAsyncThunk<
  any,
  CreateInvoiceArgs,
  { rejectValue: RejectWithValueType }
>("invoices/createInvoice", async (args, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: CREATE_INVOICE,
      variables: {
        agentId: args.agentId,
        issueDate: args.issueDate,
        remark: args.remark,
        lineItems: args.lineItems,
      },
    });
    if (response && response.data && response.data.createInvoice) {
      evictInvoiceCaches();
      await setSuccessAction(dispatch, {
        message: "Invoice Successfully Created",
      });
      return response.data.createInvoice as Invoice;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const recordInvoicePayment = createAsyncThunk<
  any,
  RecordPaymentArgs,
  { rejectValue: RejectWithValueType }
>("invoices/recordInvoicePayment", async (args, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: RECORD_INVOICE_PAYMENT,
      variables: {
        invoiceId: args.invoiceId,
        amount: args.amount,
        paymentDate: args.paymentDate,
        remark: args.remark,
      },
    });
    if (response && response.data && response.data.recordInvoicePayment) {
      evictInvoiceCaches();
      await setSuccessAction(dispatch, {
        message: "Payment Successfully Recorded",
      });
      return response.data.recordInvoicePayment as Invoice;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const cancelInvoice = createAsyncThunk<
  any,
  string,
  { rejectValue: RejectWithValueType }
>("invoices/cancelInvoice", async (id, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: CANCEL_INVOICE,
      variables: { id },
    });
    if (response && response.data && response.data.cancelInvoice) {
      evictInvoiceCaches();
      await setSuccessAction(dispatch, {
        message: "Invoice Cancelled",
      });
      return response.data.cancelInvoice as Invoice;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const fetchGovReportRates = createAsyncThunk<
  any,
  void,
  { rejectValue: RejectWithValueType }
>("invoices/fetchGovReportRates", async (_, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.query({
      query: GET_GOV_REPORT_RATES,
      fetchPolicy: "network-only",
    });
    if (response && response.data && response.data.govReportRates) {
      return response.data.govReportRates as GovReportRate[];
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const upsertGovReportRate = createAsyncThunk<
  any,
  GovReportRate,
  { rejectValue: RejectWithValueType }
>("invoices/upsertGovReportRate", async (rate, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: UPSERT_GOV_REPORT_RATE,
      variables: { country: rate.country, amount: Number(rate.amount) },
    });
    if (response && response.data && response.data.upsertGovReportRate) {
      await setSuccessAction(dispatch, {
        message: "Rate Successfully Saved",
      });
      return response.data.upsertGovReportRate as GovReportRate;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const fetchGovernmentReport = createAsyncThunk<
  any,
  GovernmentReportArgs,
  { rejectValue: RejectWithValueType }
>("invoices/fetchGovernmentReport", async (args, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.query({
      query: GET_GOVERNMENT_REPORT,
      variables: { startDate: args.startDate, endDate: args.endDate },
      fetchPolicy: "network-only",
    });
    if (response && response.data && response.data.governmentReport) {
      return response.data.governmentReport as GovernmentReport;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

async function setSuccessAction(
  dispatch: ThunkDispatch<any, any, any>,
  payload: any,
) {
  dispatch(setSuccess(payload));
  setTimeout(() => {
    dispatch(resetSuccess());
  }, 2000);
}
async function setErrorAction(
  dispatch: ThunkDispatch<any, any, any>,
  payload: any,
) {
  dispatch(setError(payload));
  setTimeout(() => {
    dispatch(resetError());
  }, 6000);
}

const initialState: InvoiceState = {
  invoicesWithCount: { totalCount: 0, invoices: [] },
  selectedInvoice: null,
  uninvoicedEmployees: [],
  govReportRates: [],
  governmentReport: null,
  loading: "idle",
  success: null,
  error: null,
};

export const invoicesSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    setSuccess: (state, { payload }) => {
      state.success = payload;
    },
    resetSuccess: (state) => {
      state.success = null;
    },
    setError: (state, { payload }) => {
      state.error = payload;
    },
    resetError: (state) => {
      state.error = null;
    },
    resetSelectedInvoice: (state) => {
      state.selectedInvoice = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInvoices.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(fetchInvoices.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      if (payload) state.invoicesWithCount = payload;
    });
    builder.addCase(fetchInvoices.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(getInvoice.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(getInvoice.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      if (payload) state.selectedInvoice = payload;
    });
    builder.addCase(getInvoice.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(fetchUninvoicedEmployees.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(fetchUninvoicedEmployees.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      if (payload) state.uninvoicedEmployees = payload;
    });
    builder.addCase(fetchUninvoicedEmployees.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(createInvoice.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(createInvoice.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      if (payload) state.selectedInvoice = payload;
    });
    builder.addCase(createInvoice.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(recordInvoicePayment.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(recordInvoicePayment.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      if (payload) state.selectedInvoice = payload;
    });
    builder.addCase(recordInvoicePayment.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(cancelInvoice.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(cancelInvoice.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      if (payload) state.selectedInvoice = payload;
    });
    builder.addCase(cancelInvoice.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(fetchGovReportRates.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(fetchGovReportRates.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      if (payload) state.govReportRates = payload;
    });
    builder.addCase(fetchGovReportRates.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(upsertGovReportRate.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(upsertGovReportRate.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      if (payload) {
        const index = state.govReportRates.findIndex(
          (r) => r.country === payload.country,
        );
        if (index >= 0) state.govReportRates[index] = payload;
        else state.govReportRates.push(payload);
      }
    });
    builder.addCase(upsertGovReportRate.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(fetchGovernmentReport.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(fetchGovernmentReport.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      if (payload) state.governmentReport = payload;
    });
    builder.addCase(fetchGovernmentReport.rejected, (state) => {
      state.loading = "idle";
    });
  },
});

export const {
  setSuccess,
  resetSuccess,
  setError,
  resetError,
  resetSelectedInvoice,
} = invoicesSlice.actions;

export const selectInvoices = (state: RootState) => state.invoices;

export default invoicesSlice.reducer;
